import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../../util/Util';
import Invoice from "../../invoice/InvoiceReport";
import { useAuth } from "../../../hooks/useAuth";
import { useData } from "../../../hooks/useData";
import { useUI } from "../../../providers/MoleUIContext";
import { useDateRange, periodRequestConfig } from "../../../hooks/useDateRange";
import { toast } from "react-toastify";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, Button, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../../util/constants";
import TableView from "../../../components/TableView";
import { AssignmentReturn, Delete, DeleteOutline, Edit, Refresh } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../../components/ExportMenuButton";
import ReturnSaleModal from "../ReturnSaleModal";
import { createSign } from "crypto";
import { createSignCell } from "../../../util/cellFormatters";
import { useAppSocket } from "../../../providers/AppSocketContext";
import { parseISODay } from "../../../util/businessClock";

import { roundQty } from "../../../util/quantity";
import { canAmend, canDrop, canRecord } from '../../../util/access';
const Sale = () => {
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [toView, setToView] = useState<any>(null);
	const [toReturn, setToReturn] = useState<any>(null);

	const { api, session, access } = useAuth();
	const { appSettings } = useAppSocket();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading } = useData(LocalStorageKeys.SALES_RECORDS);
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(LocalStorageKeys.SALES_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (!loadFromIndexedDB() && period && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [period]);

	const fetchData = async (useLoadModal: boolean = true) => {
		setLoading(true);

		const res = await api.get("sales", {
			...(periodRequestConfig(period))
		});

		if (res) {
			if (res.status === 200) {
				saveFromServer(res.data, period);
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"), //render Date as a string
			filterVariant: 'date-range',
		},
		{
			header: t("common:tables.type"),
			accessorKey: "inv_type",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.itemCode"),
			accessorKey: "item_code",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.item"),
			accessorKey: "item_name",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.quantity"),
			accessorKey: "quantity",
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.quantity);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.amount"),
			accessorKey: "amount",
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.total"),
			accessorKey: "total",
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.total);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.agent"),
			accessorKey: "agent",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.client"),
			accessorKey: "client",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.invoiceNo"),
			accessorKey: "reference",
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => setToView(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.pos"),
			accessorKey: "depot",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.sector"),
			accessorKey: "sector",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
	], [t]);

	const handleDelete = async () => {
		if (toDelete) {
			setLoadingMessage("DELETE");

			const res = await api.delete(`/sales/${toDelete.batch}`);

			if (res.status === 204) {
				toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
				fetchData();
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(handleDelete);
			}

			setLoadingMessage(null);
			setToDelete(null);
		}
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length > 0) {
			const payload = { batches: toDeleteBulk };

			setLoadingMessage("DELETE");

			const res = await api.post("/sales/delete", payload);

			if (res) {
				if (res.status === 204) {
					toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
					tableRef.current?.resetRowSelection();
					fetchData();
				} else if (res.status === 409) {
					toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
				} else if (res.status === 401 || res.status === 422) {
					queueReRun(handleBulkDelete);
				}
			}

			setLoadingMessage(null);
			setToDeleteBulk([]);
		}
	};

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
				stateKey={LocalStorageKeys.SALES_RECORDS}
				posting="sale"
					dataScope="sale"
					header={t("common:sales.headers.records")}
					data={data}
					columns={columns}
					totals={[
						{ key: "quantity", label: t("common:tables.quantity") },
						{ key: "total", label: t("common:tables.total") },
					]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					getRowSx={(row) => Util.compareStrings(row.original?.inv_type || "", "Return") ? {
						backgroundColor: (theme: any) => theme.palette.mode === 'dark'
							? 'rgba(255, 167, 38, 0.12)'
							: 'rgba(255, 167, 38, 0.08)',
						borderLeft: '3px solid',
						borderLeftColor: 'warning.main',
					} : {}}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							{
								[
									...new Set(
										table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
									),
								].length === 1 && (
									<Tooltip title={t("common:ui.tooltips.editSelectedInvoice")}>
										<IconButton
											onClick={() => {
												if (allowOrWarn(canAmend(access, 'sale'))) {
													navigate("/pos", { state: { payload: table.getSelectedRowModel().flatRows[0].original, src: location.pathname } });
												}
											}}
											color="primary">
											<Edit />
										</IconButton>
									</Tooltip>
								)
							}
							{table.getIsSomeRowsSelected() && (
								<Tooltip title={t("common:ui.tooltips.deleteSelection")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn((canDrop(access, 'sale', 'delete_bulk') || canDrop(access, 'return', 'delete_bulk')))) {
												const rowsBatches = [
													...new Set(
														table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(table.getSelectedRowModel().flatRows[0].original);
												} else {
													tableRef.current = table;

													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error">
										<Delete />
									</IconButton>
								</Tooltip>
							)}
							<ExportMenuButton
								prefix="sales"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						!toDelete ? (
							<MenuItem
								key={0}
								onClick={() => {
									if (allowOrWarn(canAmend(access, 'sale'))) {
										navigate("/pos", { state: { payload: row.original, src: location.pathname } });
									}
									closeMenu();
								}}
								sx={{
									color: 'primary.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'primary.main',
								}}>
									<Edit />
								</ListItemIcon>
								{t("common:ui.buttons.edit")}
							</MenuItem>
						) : null,
						!toDelete && Util.compareStrings(String(row.original.inv_type || ''), "Sale") && !String(row.original.batch || "").startsWith("ret:") ? (
							<MenuItem
								key="return"
								onClick={() => {
									if (allowOrWarn(canRecord(access, 'return'))) {
										setToReturn(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'warning.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'warning.main',
								}}>
									<AssignmentReturn />
								</ListItemIcon>
								{t("common:ui.buttons.return")}
							</MenuItem>
						) : null,
						!toDelete ? (
							<MenuItem
								key={1}
								onClick={() => {
									if (allowOrWarn((canDrop(access, 'sale') || canDrop(access, 'return')))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'error.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'error.main',
								}}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>
			</LocalizationProvider >

			{/* Invoice Modal */
				toView &&
				<Invoice
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			}

			{/* Return Sale Modal */
				toReturn &&
				<ReturnSaleModal
					isOpen={!!toReturn}
					toggle={() => setToReturn(null)}
					bundle={toReturn}
					onSuccess={() => fetchData()}
				/>
			}

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={toDelete ? handleDelete : handleBulkDelete}
					onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]) }}
					isOpen={toDelete || toDeleteBulk.length > 0}
					setIsOpen={() => { }}
					title={<><DeleteOutline sx={{ color: "red" }} /> {t("common:dialog.deleteConfirmation")}</>}
					message={
						toDelete ?
							<>
								{t("common:sales.confirm.deleteSingleStart")}
								<b><u>{Util.formatCurrency(toDelete?.amount, appSettings?.BIZ_CURRENCY?.value || "USD")}</u></b>&nbsp;
								{t("common:sales.confirm.deleteSingleTo")} <b><u>{toDelete?.client}</u></b>&nbsp;
								{t("common:sales.confirm.deleteSingleAndPayments")}&nbsp;
								{t("common:sales.confirm.deleteSingleDate")} <b><u>{Util.formatDate(toDelete?.date, "full")}</u></b>&nbsp;
								, {t("common:sales.confirm.deleteSingleReference")} <button
									style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
									onClick={() => setToView(toDelete)}
								>
									<b><u>{toDelete?.reference}</u></b>
								</button>!
							</>
							:
							<>
								{t("common:sales.confirm.deleteBulkWithPayments", { count: toDeleteBulk.length })}
							</>
					}
					confirmBtnColor="error"
					confirmBtnVariant="contained"
					confirmBtnText={<>{t("common:ui.buttons.delete")}</>}
				/>
			}
		</>
	);
};

export default Sale;
